import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { isClinicianAssignedToStage } from '../utils/clinicianPermissions';

/**
 * Custom hook that determines whether the current authenticated clinician
 * has edit permission for a given assessment stage (CI, SSI, DD, PAD).
 *
 * It compares the logged-in user's UID against the clinician assigned to
 * `patientData.clinicians.clinician<Stage>`. Returns `false` until
 * both `patientDataModel` and the Firebase auth user are available, so
 * the UI safely defaults to read-only while data is loading.
 *
 * An optional `options.previewMode` flag forces read-only even when the user
 * is assigned. This supports the Preview button on overview cards, which
 * navigates to a module with `?preview=true` — the page reads that param,
 * passes it here, and gets `canEdit = false` regardless of assignment.
 *
 * Usage:
 *   const canEdit = useCanEditStage(patientDataModel, 'PAD');
 *   const canEdit = useCanEditStage(patientDataModel, 'CI', { previewMode: true });
 *
 * @param {Object|null} patientDataModel - The patient data model from PatientDataContext.
 * @param {string} stage - One of 'CI', 'SSI', 'DD', 'PAD'.
 * @param {Object} [options]
 * @param {boolean} [options.previewMode=false] - When true, forces canEdit to false.
 * @returns {boolean} Whether the current user can edit this stage.
 */
export const useCanEditStage = (patientDataModel, stage, { previewMode = false } = {}) => {
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    if (previewMode) {
      setCanEdit(false);
      return;
    }

    const currentUser = auth.currentUser;
    if (currentUser && patientDataModel) {
      setCanEdit(isClinicianAssignedToStage(patientDataModel, currentUser.uid, stage));
    } else {
      setCanEdit(false);
    }
  }, [patientDataModel, stage, previewMode]);

  return canEdit;
};
